import React, { Fragment, useCallback, useEffect, useState } from "react";
import { Skeleton } from "@material-ui/lab";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { notBlank } from "../util/ext";
import {
  compose,
  equals,
  filter,
  find,
  prop,
  propEq,
  sortBy,
  toLower,
  not,
} from "ramda";
import TextField from "@material-ui/core/TextField";
import { identity } from "rxjs";
import { Box } from "@material-ui/core";
import { FixedSizeList } from "react-window";
import PropTypes from "prop-types";
import Typography from "@material-ui/core/Typography";
import { useContentProviderEndpoints } from "./discovery-reporting/processingDiscoveryServices";

const LISTBOX_PADDING = 4; // px

function renderRow(props) {
  const { data, index, style } = props;
  return React.cloneElement(data[index], {
    style: {
      ...style,
      top: style.top + LISTBOX_PADDING,
    },
  });
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

// Adapter for react-window
const ListboxComponent = React.forwardRef(function ListboxComponent(
  props,
  ref,
) {
  const { children, ...other } = props;
  const itemData = React.Children.toArray(children);
  const itemCount = itemData.length;
  const itemSize = 50;

  const getHeight = () => {
    if (itemCount > 8) {
      return 8 * itemSize;
    }
    return itemSize * itemCount;
  };

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <FixedSizeList
          style={{
            borderStyle: "solid",
            borderColor: "#F7F9FA",
            borderWidth: 2,
          }}
          itemData={itemData}
          height={getHeight() + 2 * LISTBOX_PADDING}
          width="100%"
          outerElementType={OuterElementType}
          innerElementType="ul"
          itemSize={itemSize}
          overscanCount={5}
          itemCount={itemCount}
        >
          {renderRow}
        </FixedSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});

ListboxComponent.propTypes = {
  children: PropTypes.node,
};

const ContentProviderEndpointAutoComplete = props => {
  const {
    onChange,
    onBlur,
    value,
    label = "Content Provider",
    filterFunction = identity,
    activeOnly = false,
    autoFocus = false,
    disabled = false,
    helperText,
    error,
  } = props;
  const contentProviderEndpoints = useContentProviderEndpoints();
  const [activeEndpoints, setActiveEndpoints] = useState([]);
  const [selectedEndpoint, setSelectedEndpoint] = useState(null);

  const filterFunctionCallback = useCallback(filterFunction, []);

  useEffect(() => {
    if (contentProviderEndpoints.isSuccess) {
      const endpointList = sortBy(
        compose(toLower, prop("name")),
        filter(
          endpoint => !activeOnly || endpoint.enabled,
          filter(filterFunctionCallback, contentProviderEndpoints.data),
        ),
      );

      setActiveEndpoints(endpointList);
      setSelectedEndpoint(
        find(propEq("contentProviderEndpointId", value), endpointList),
      );
    }
  }, [
    contentProviderEndpoints.isSuccess,
    contentProviderEndpoints.data,
    activeOnly,
    filterFunctionCallback,
    value,
  ]);

  const handleEndpointSelect = useCallback(
    (_event, endpoint) => {
      setSelectedEndpoint(endpoint);
      onChange(endpoint ? endpoint.contentProviderEndpointId : null);
    },
    [onChange],
  );

  return (
    <Fragment>
      {contentProviderEndpoints.isLoading ? (
        <Skeleton variant="text" />
      ) : (
        <Autocomplete
          disabled={disabled}
          disableListWrap
          options={activeEndpoints}
          value={selectedEndpoint || null}
          ListboxComponent={ListboxComponent}
          getOptionLabel={org => notBlank(org) && `${org.name}`}
          getOptionSelected={(opt, val) =>
            notBlank(val) &&
            equals(opt.contentProviderEndpointId, val.contentProviderEndpointId)
          }
          renderOption={(option, { selected }) => (
            <Box>
              <Box fontSize="1.4rem">
                <Typography
                  color={
                    notBlank(option.enabled) && not(option.enabled)
                      ? "error"
                      : "inherit"
                  }
                >
                  {option.name}
                </Typography>
              </Box>
              <Box fontSize="1.2rem">
                <Typography
                  color={
                    notBlank(option.enabled) && not(option.active)
                      ? "error"
                      : "inherit"
                  }
                >
                  {`(${option.contentProviderEndpointId})`}
                </Typography>
              </Box>
            </Box>
          )}
          onChange={handleEndpointSelect}
          onBlur={onBlur}
          renderInput={params => {
            params.inputProps["aria-autocomplete"] = "none";
            params.inputProps["id"] = null;
            return (
              <TextField
                {...params}
                autoFocus={autoFocus}
                type="text"
                name={"content-provider-endpoint-select-" + Math.random()}
                variant="standard"
                label={label}
                error={error}
                helperText={helperText}
                fullWidth
              />
            );
          }}
        />
      )}
    </Fragment>
  );
};

ContentProviderEndpointAutoComplete.propTypes = {
  label: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  filterFunction: PropTypes.func,
  activeOnly: PropTypes.bool,
  autoFocus: PropTypes.bool,
  helperText: PropTypes.string,
  error: PropTypes.bool,
};

export default ContentProviderEndpointAutoComplete;
